import React from "react";
import { useSearchParams } from "react-router-dom";
import styles from "../components/Dashboard.module.css";
import ConfirmationModal from "../components/ConfirmationModal";
import { runsApi, RunRow, RunDetail, ListRunsFilters } from "../services/runsApi";
import { agentsApi, Agent } from "../services/agentsApi";
import { datasetCoverageApi, AgentDatasetPair } from "../services/datasetCoverageApi";

const PAGE_SIZE = 50;

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "", label: "Any status" },
  { value: "queued", label: "Queued" },
  { value: "running", label: "Running" },
  { value: "cancelling", label: "Cancelling" },
  { value: "recovering_failure", label: "Recovering failure" },
  { value: "recovering_cancellation", label: "Recovering cancellation" },
  { value: "cancelled", label: "Cancelled" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "config_error", label: "Config error" },
];

const SOURCE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "", label: "Any source" },
  { value: "manual", label: "Manual" },
  { value: "scheduler", label: "Scheduler" },
  { value: "webhook", label: "Webhook" },
];

function buildListFilters(params: {
  agentId: string;
  datasetId: string;
  status: string;
  source: string;
  page: number;
}): ListRunsFilters {
  return {
    page: params.page,
    limit: PAGE_SIZE,
    agent_id: params.agentId || undefined,
    dataset_id: params.datasetId || undefined,
    status: (params.status || undefined) as ListRunsFilters["status"],
    source: (params.source || undefined) as ListRunsFilters["source"],
  };
}

const RunDataset: React.FC<{ run: RunRow }> = ({ run }) => {
  const baseDataset = run.baseDatasetLabel || run.baseDatasetId || run.datasetLabel;
  const executionDataset = run.datasetLabel || run.datasetId;

  const copyExecutionDataset = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    void navigator.clipboard?.writeText(run.datasetId);
  };

  return (
    <div className={styles.runsDatasetCell}>
      <div className={styles.runsDatasetPrimary} title={baseDataset}>
        {baseDataset}
      </div>
      {run.baseDatasetId && run.baseDatasetId !== run.datasetId ? (
        <div className={styles.runsDatasetSecondary}>
          <span className={styles.runsDatasetSecondaryText} title={executionDataset}>
            Execution: {executionDataset}
          </span>
          <button
            type="button"
            className={styles.runsDatasetCopyBtn}
            title="Copy execution dataset ID"
            aria-label={`Copy execution dataset ID ${run.datasetId}`}
            onClick={copyExecutionDataset}
          >
            Copy
          </button>
        </div>
      ) : null}
    </div>
  );
};

const RunsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const urlFiltersApplied = React.useRef(false);
  const [runs, setRuns] = React.useState<RunRow[]>([]);
  const [totalRuns, setTotalRuns] = React.useState(0);
  const [agents, setAgents] = React.useState<Agent[]>([]);
  const [pairs, setPairs] = React.useState<AgentDatasetPair[]>([]);
  const [metaLoading, setMetaLoading] = React.useState(true);
  const [runsHydrated, setRunsHydrated] = React.useState(false);

  const [filterAgentId, setFilterAgentId] = React.useState("");
  const [filterDatasetId, setFilterDatasetId] = React.useState("");
  const [filterStatus, setFilterStatus] = React.useState("");
  const [filterSource, setFilterSource] = React.useState("");
  const [page, setPage] = React.useState(1);

  const [selectedRunId, setSelectedRunId] = React.useState("");
  const [selectedDetail, setSelectedDetail] = React.useState<RunDetail | null>(null);
  const [feedback, setFeedback] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [cancellingRunId, setCancellingRunId] = React.useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<RunRow | null>(null);
  const [deletingRunId, setDeletingRunId] = React.useState<string | null>(null);
  const [isDetailOpen, setIsDetailOpen] = React.useState(false);
  const [detailLoading, setDetailLoading] = React.useState(false);

  const filterParams = React.useMemo(
    () => ({
      agentId: filterAgentId,
      datasetId: filterDatasetId,
      status: filterStatus,
      source: filterSource,
      page,
    }),
    [filterAgentId, filterDatasetId, filterStatus, filterSource, page],
  );

  React.useEffect(() => {
    if (urlFiltersApplied.current) return;
    urlFiltersApplied.current = true;
    const source = searchParams.get("source");
    const agentId = searchParams.get("agent_id") ?? searchParams.get("agentId");
    const datasetId = searchParams.get("dataset_id") ?? searchParams.get("datasetId");
    const status = searchParams.get("status");
    if (source && SOURCE_OPTIONS.some((o) => o.value === source)) setFilterSource(source);
    if (agentId) setFilterAgentId(agentId);
    if (datasetId) setFilterDatasetId(datasetId);
    if (status && STATUS_OPTIONS.some((o) => o.value === status)) setFilterStatus(status);
  }, [searchParams]);

  const datasetOptions = React.useMemo(() => {
    const src = filterAgentId ? pairs.filter((p) => p.agent_id === filterAgentId) : pairs;
    const map = new Map<string, string>();
    for (const p of src) {
      map.set(p.dataset_id, p.dataset_name || p.dataset_id);
    }
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [pairs, filterAgentId]);

  React.useEffect(() => {
    if (!filterDatasetId) return;
    const ok = datasetOptions.some(([id]) => id === filterDatasetId);
    if (!ok) setFilterDatasetId("");
  }, [filterAgentId, datasetOptions, filterDatasetId]);

  React.useEffect(() => {
    let mounted = true;
    const loadMeta = async () => {
      setMetaLoading(true);
      try {
        const [a, p] = await Promise.all([agentsApi.listAgents(), datasetCoverageApi.listPairs()]);
        if (!mounted) return;
        setAgents(a);
        setPairs(p.data);
      } catch {
        if (!mounted) return;
        setError("Failed to load filter options (agents / pairs).");
      } finally {
        if (mounted) setMetaLoading(false);
      }
    };
    void loadMeta();
    return () => {
      mounted = false;
    };
  }, []);

  const loadRuns = React.useCallback(async () => {
    try {
      const { runs: rows, total } = await runsApi.listRuns(buildListFilters(filterParams));
      setRuns(rows);
      setTotalRuns(total);
      setError(null);
      return rows;
    } catch {
      setError("Failed to load runs.");
      return [];
    }
  }, [filterParams]);

  React.useEffect(() => {
    let mounted = true;
    const refresh = async () => {
      await loadRuns();
      if (mounted) setRunsHydrated(true);
    };
    void refresh();
    return () => {
      mounted = false;
    };
  }, [loadRuns]);

  React.useEffect(() => {
    if (runs.length === 0) {
      setSelectedRunId("");
      return;
    }
    setSelectedRunId((prev) => (prev && runs.some((r) => r.id === prev) ? prev : runs[0].id));
  }, [runs]);

  React.useEffect(() => {
    let mounted = true;
    if (!selectedRunId) return;
    const loadDetail = async () => {
      setDetailLoading(true);
      const detail = await runsApi.getRunDetail(selectedRunId);
      if (mounted) {
        setSelectedDetail(detail);
        setDetailLoading(false);
      }
    };
    void loadDetail();
    return () => {
      mounted = false;
    };
  }, [selectedRunId]);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsDetailOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  React.useEffect(() => {
    const poll = window.setInterval(() => {
      void loadRuns();
    }, 1500);
    return () => window.clearInterval(poll);
  }, [loadRuns]);

  const selectedRun = runs.find((run) => run.id === selectedRunId) || null;

  const agentLabel = React.useCallback(
    (id: string) => agents.find((a) => a.id === id)?.name ?? id,
    [agents],
  );

  const cancelRun = async (runId: string) => {
    setFeedback(null);
    setError(null);
    setCancellingRunId(runId);
    try {
      await runsApi.cancelRun(runId);
      await loadRuns();
      setFeedback(`Run ${runId} cancelled.`);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to cancel run.");
    } finally {
      setCancellingRunId(null);
    }
  };

  const confirmDeleteRun = async () => {
    if (!deleteTarget) return;
    setFeedback(null);
    setError(null);
    setDeletingRunId(deleteTarget.id);
    try {
      await runsApi.deleteRun(deleteTarget.id);
      if (selectedRunId === deleteTarget.id) {
        setSelectedRunId("");
        setSelectedDetail(null);
        setIsDetailOpen(false);
      }
      setDeleteTarget(null);
      await loadRuns();
      setFeedback(`Run ${deleteTarget.id} deleted from TrustForge.`);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to delete run.");
    } finally {
      setDeletingRunId(null);
    }
  };

  const renderRunActions = (run: RunRow) => (
    <div className={styles.runsActionsCell}>
      <button
        type="button"
        className={`${styles.metricsSecondaryBtn} ${styles.runsViewDetailsBtn}`}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedRunId(run.id);
          setIsDetailOpen(true);
        }}
      >
        View details
      </button>
      {run.status === "running" || run.status === "queued" ? (
        <button
          type="button"
          className={styles.metricsDangerBtn}
          data-testid={`cancel-run-${run.id}`}
          disabled={cancellingRunId != null || deletingRunId != null}
          onClick={(e) => {
            e.stopPropagation();
            void cancelRun(run.id);
          }}
        >
          Cancel run
        </button>
      ) : null}
      {run.status === "cancelling" ? <span>Cancelling...</span> : null}
      <button
        type="button"
        className={styles.metricsDangerBtn}
        data-testid={`delete-run-${run.id}`}
        disabled={cancellingRunId != null || deletingRunId != null}
        onClick={(e) => {
          e.stopPropagation();
          setDeleteTarget(run);
        }}
      >
        Delete
      </button>
    </div>
  );

  const maxPage = Math.max(1, Math.ceil(totalRuns / PAGE_SIZE));
  const effectivePage = Math.min(page, maxPage);
  React.useEffect(() => {
    if (page !== effectivePage) setPage(effectivePage);
  }, [page, effectivePage]);

  const resetFiltersToFirstPage = () => setPage(1);

  if (metaLoading || !runsHydrated) {
    return (
      <div className={styles.dashboard}>
        <section className={styles.dashboardLoading} aria-live="polite" aria-busy="true">
          <div className={styles.dashboardLoadingSpinner} />
          <p className={styles.dashboardLoadingText}>Loading runs...</p>
        </section>
      </div>
    );
  }

  const rangeStart = totalRuns === 0 ? 0 : (effectivePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(effectivePage * PAGE_SIZE, totalRuns);

  return (
    <div className={styles.runsPage}>
      <section className={styles.runsTableCard}>
        <h1 className={styles.runsTitle}>Runs</h1>

        <div className={styles.runsFiltersToolbar} role="search" aria-label="Filter runs">
          <label className={styles.runsFilterField}>
            Agent
            <select
              className={`${styles.metricsSelect} ${styles.metricsSelectArrow}`.trim()}
              value={filterAgentId}
              onChange={(e) => {
                setFilterAgentId(e.target.value);
                resetFiltersToFirstPage();
              }}
            >
              <option value="">All agents</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.runsFilterField}>
            Dataset
            <select
              className={`${styles.metricsSelect} ${styles.metricsSelectArrow}`.trim()}
              value={filterDatasetId}
              onChange={(e) => {
                setFilterDatasetId(e.target.value);
                resetFiltersToFirstPage();
              }}
            >
              <option value="">All datasets</option>
              {datasetOptions.map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.runsFilterField}>
            Status
            <select
              className={`${styles.metricsSelect} ${styles.metricsSelectArrow}`.trim()}
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                resetFiltersToFirstPage();
              }}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value || "any"} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.runsFilterField}>
            Source
            <select
              className={`${styles.metricsSelect} ${styles.metricsSelectArrow}`.trim()}
              value={filterSource}
              onChange={(e) => {
                setFilterSource(e.target.value);
                resetFiltersToFirstPage();
              }}
            >
              {SOURCE_OPTIONS.map((o) => (
                <option key={o.value || "any-src"} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className={styles.diagnosticsTableWrap}>
          <table className={styles.diagnosticsTable}>
            <thead>
              <tr>
                <th>Run ID</th>
                <th>Agent</th>
                <th>Datasets</th>
                <th>Mode</th>
                <th>Trigger</th>
                <th>Status</th>
                <th>Counters</th>
                <th>Timestamps</th>
                <th className={styles.runsActionsHeader}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {runs.length > 0 ? (
                runs.map((run) => (
                  <tr
                    key={run.id}
                    className={selectedRunId === run.id ? styles.runsRowActive : ""}
                    onClick={() => setSelectedRunId(run.id)}
                  >
                    <td>{run.id}</td>
                    <td>{agentLabel(run.agentId)}</td>
                    <td>
                      <RunDataset run={run} />
                    </td>
                    <td>{run.mode}</td>
                    <td>{run.triggerSource}</td>
                    <td>{run.status}</td>
                    <td>
                      total:{run.totalItems} / queued:{run.queuedItems ?? 0} / processing:
                      {run.processingItems ?? 0} / pass:{run.passedItems} / fail:{run.failedItems}
                      {(run.skippedItems ?? 0) > 0 ? ` / skipped:${run.skippedItems}` : ""}
                    </td>
                    <td>
                      start: {new Date(run.startedAt).toLocaleString()}
                      <br />
                      end: {run.finishedAt ? new Date(run.finishedAt).toLocaleString() : "-"}
                    </td>
                    <td className={styles.runsActionsHeader}>{renderRunActions(run)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9}>No runs found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalRuns > 0 ? (
          <div className={styles.runsPaginationRow}>
            <span className={styles.runsPaginationMeta}>
              Showing {rangeStart}–{rangeEnd} of {totalRuns}
            </span>
            <div className={styles.runsPaginationButtons}>
              <button
                type="button"
                className={styles.metricsSecondaryBtn}
                disabled={effectivePage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className={styles.metricsSecondaryBtn}
                disabled={effectivePage >= maxPage}
                onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        ) : null}

        {feedback ? <div className={styles.metricsSuccessBox}>{feedback}</div> : null}
        {error ? (
          <div className={styles.metricsErrorBox}>
            <p>{error}</p>
          </div>
        ) : null}
      </section>

      {isDetailOpen ? (
        <div
          className={styles.runsModalOverlay}
          onClick={() => setIsDetailOpen(false)}
          role="presentation"
        >
          <section
            className={styles.runsDetailModal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Run detail review"
          >
            <div className={styles.runsDetailHeader}>
              <h2 className={styles.runsDetailTitle}>Run detail review</h2>
            </div>
            {detailLoading ? (
              <div className={styles.runsModalLoadingOverlay} aria-live="polite" aria-busy="true">
                <div className={styles.dashboardLoadingSpinner} />
                <p className={styles.dashboardLoadingText}>Loading run details...</p>
              </div>
            ) : null}
            {selectedRun ? (
              <div className={styles.runsDetailMeta}>
                <p>
                  <strong>Run:</strong> {selectedRun.id}
                </p>
                <p>
                  <strong>Agent:</strong> {agentLabel(selectedRun.agentId)}
                </p>
                <div className={styles.runsDetailDataset}>
                  <strong>Datasets:</strong>
                  <RunDataset run={selectedRun} />
                </div>
                <p>
                  <strong>Status:</strong> {selectedRun.status}
                </p>
                <p>
                  <strong>Trigger:</strong> {selectedRun.triggerSource}
                </p>
              </div>
            ) : null}

            <div className={styles.diagnosticsTableWrap}>
              <table className={styles.diagnosticsTable}>
                <thead>
                  <tr>
                    <th>Item ID</th>
                    <th>Item status</th>
                    <th>Summary</th>
                    <th>Error details</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedDetail?.items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.id}</td>
                      <td>{item.status}</td>
                      <td>{item.summary}</td>
                      <td>{item.error || "-"}</td>
                    </tr>
                  )) || (
                    <tr>
                      <td colSpan={4}>No run selected.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className={styles.runsDetailFooter}>
              <button
                type="button"
                className={styles.metricsSecondaryBtn}
                onClick={() => setIsDetailOpen(false)}
              >
                Close
              </button>
            </div>
          </section>
        </div>
      ) : null}

      <ConfirmationModal
        isOpen={deleteTarget !== null}
        onClose={() => {
          if (!deletingRunId) setDeleteTarget(null);
        }}
        onConfirm={() => void confirmDeleteRun()}
        title="Delete run"
        message={
          deleteTarget
            ? `Delete run ${deleteTarget.id} for ${agentLabel(deleteTarget.agentId)}? This removes the run and its items from TrustForge only (Langfuse traces are not affected).`
            : ""
        }
        confirmText="Delete run"
        cancelText="Cancel"
        isDestructive
        loading={deletingRunId !== null}
      />
    </div>
  );
};

export default RunsPage;
